import { emailRegex } from '../../lib/validations/commonSchemas';

// Brand onboarding keeps its place per browser tab in sessionStorage (currentStep,
// verifiedCompanyName). The Step 1 email has to survive a reload the same way:
// steps 2 and 3 and the final save all send it, and the backend rejects an empty
// email (422). When the server-side restore failed, a reload used to leave the
// page on step 2 with no email, so every retry failed.

const EMAIL_KEY = 'email';
const VERIFIED_KEY = 'emailVerifiedFor';

type SessionStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export const EMAIL_LOST_MESSAGE =
  'Please confirm your email address again. It was lost when the page reloaded, so we could not save this step.';

function defaultStore(): SessionStore | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
}

export function savedEmail(store: SessionStore | null = defaultStore()): string {
  return store?.getItem(EMAIL_KEY) || '';
}

/** True when the saved email is the one that was verified with an OTP in this tab. */
export function savedEmailVerified(store: SessionStore | null = defaultStore()): boolean {
  const email = savedEmail(store);
  return !!email && store?.getItem(VERIFIED_KEY) === email;
}

export function saveEmail(email: string, verified: boolean, store: SessionStore | null = defaultStore()): void {
  if (!store) return;
  if (email) store.setItem(EMAIL_KEY, email);
  else store.removeItem(EMAIL_KEY);
  if (email && verified) store.setItem(VERIFIED_KEY, email);
  else store.removeItem(VERIFIED_KEY);
}

export function isUsableEmail(email: string | null | undefined): boolean {
  const value = (email || '').trim();
  return !!value && emailRegex.test(value);
}

/** The save endpoint's "email id is alredy taken" (sic) answer, any spelling. */
export function isEmailTakenMessage(message: string | null | undefined): boolean {
  return /\btaken\b/i.test(message || '');
}

/** A save the backend rejected because of the email (422 on the email field). */
export function isEmailRejection(error: unknown): boolean {
  const message = String((error as { message?: unknown })?.message || '').toLowerCase();
  return message.includes('email');
}
